import * as ActionTypes from '../actions/actionTypes';
import {calculateTableData, convertToGraphData, PREFIX_WIND_MAG_KPH} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';
import {FOREST_TYPE} from '../utils/behaviour_models/vesta_mk2/constants';
import {totalFuelLoad} from '../utils/behaviour_models/vesta_mk2/shared';
import {
  calculateDIR,
  calculateFFM_C,
  calculateFFM_DT,
  calculateFFM_NT,
  calculateFFM_PM,
  calculatePhiFa,
  calculatePhiMd,
  calculatePhiTheta,
  calculateWind2m,
  getDf,
  getKbdi,
  surfacePlusNearLoad,
  totalFuelLoad as calcTotalFuelLoad
} from '../utils/behaviour_models/vesta_mk2/shared';
import {calculateWetDf} from '../utils/behaviour_models/vesta_mk2/wet';

const _hasDf = (wd) => typeof getDf(wd) !== 'undefined';

const _calculateDF_WET = (wd, vars) => {
  const df = getDf(wd);
  const kbdi = getKbdi(wd);
  if (typeof df === 'undefined' ||
      typeof kbdi === 'undefined' ||
      vars.forest_type !== FOREST_TYPE.WET) {
    return undefined;
  }
  return calculateWetDf(df, vars.wind_reduction_factor, kbdi, vars.aspect, vars.slope_steepness);
};

const _calculateWIND_2M = (wd, vars) => {
  if (!_hasDf(wd)) {
    return undefined;
  }
  return calculateWind2m(wd, vars.wind_reduction_factor);
};

const _calculatePHI_MD = (wd, vars) => {
  if (!_hasDf(wd)) {
    return undefined;
  }
  if (vars.forest_type === FOREST_TYPE.WET) {
    return calculatePhiMd(calculateFFM_NT(wd));
  }
  return calculatePhiMd(calculateFFM_C(wd, vars));
};

const _calculatePHI_FA = (wd, vars) => {
  if (!_hasDf(wd)) {
    return undefined;
  }
  if (vars.forest_type === FOREST_TYPE.WET) {
    const dfWet = _calculateDF_WET(wd, vars);
    if (typeof dfWet === 'undefined') {
      return undefined;
    }
    return calculatePhiFa(dfWet);
  }
  return calculatePhiFa(getDf(wd));
};

const _calculatePHI_M = (wd, vars) => {
  const phiMd = _calculatePHI_MD(wd, vars);
  const phiFa = _calculatePHI_FA(wd, vars);
  if (typeof phiMd === 'undefined' || typeof phiFa === 'undefined') {
    return undefined;
  }
  return phiMd * phiFa;
};

const _calculatePHI_THETA = (wd, vars) => {
  if (!_hasDf(wd)) {
    return undefined;
  }
  return calculatePhiTheta(vars.slope);
};

const _phaseFrosPh1 = (wind2m, surfaceNearLoad, phiM, phiTheta) => {
  if (wind2m > 2) {
    const fuelTerm = Math.pow(surfaceNearLoad / 10, 0.79928);
    const windTerm = 0.05024 * Math.pow(wind2m - 1, 0.92628);
    return (0.03 + windTerm * fuelTerm) * 1000 * phiM * phiTheta;
  }
  return 0.03 * phiM * phiTheta * 1000;
};

const _phaseFrosPh2 = (wind2m, surfaceNearLoad, elevatedHeight, phiM, phiTheta) => {
  if (wind2m > 2) {
    const fuelTerm = Math.pow(surfaceNearLoad / 10, 0.46722);
    const windTerm = 0.19591 * Math.pow(wind2m, 0.82569);
    return windTerm * fuelTerm * Math.pow(elevatedHeight, 0.495) * 1000 * phiM * phiTheta;
  }
  return 0.03 * 1000 * phiM * phiTheta;
};

const _phaseFrosPh3 = (wind10m, phiM, phiTheta) => {
  return 0.05235 * Math.pow(wind10m, 1.19128) * 1000 * phiM * phiTheta;
};

const _probabilityPh2 = (wind2m, phiM, surfaceNearLoad) => {
  if (surfaceNearLoad < 1) {
    return 0;
  }
  const x = -23.9315 + 1.7033 * wind2m + 12.0822 * phiM + 0.95236 * surfaceNearLoad;
  return 1 / (1 + Math.exp(-x));
};

const _probabilityPh3 = (frosPh2, wind10m, phiM) => {
  if (frosPh2 < 300) {
    return 0;
  }
  const x = -32.3074 + 0.2951 * wind10m + 26.8734 * phiM;
  return 1 / (1 + Math.exp(-x));
};

const _expectedFros = (frosPh1, frosPh2, frosPh3, pPh2, pPh3) => {
  if (pPh2 < 0.5) {
    return frosPh1 * (1 - pPh2) + frosPh2 * pPh2;
  }
  return frosPh1 * (1 - pPh2) + frosPh2 * pPh2 * (1 - pPh3) + frosPh3 * pPh3;
};

const _calculateFROS_PH1 = (wd, vars) => {
  const wind2m = _calculateWIND_2M(wd, vars);
  const phiM = _calculatePHI_M(wd, vars);
  const phiTheta = _calculatePHI_THETA(wd, vars);
  if (typeof wind2m === 'undefined' || typeof phiM === 'undefined' || typeof phiTheta === 'undefined') {
    return undefined;
  }
  return _phaseFrosPh1(wind2m, surfacePlusNearLoad(vars), phiM, phiTheta);
};

const _calculateFROS_PH2 = (wd, vars) => {
  const wind2m = _calculateWIND_2M(wd, vars);
  const phiM = _calculatePHI_M(wd, vars);
  const phiTheta = _calculatePHI_THETA(wd, vars);
  if (typeof wind2m === 'undefined' || typeof phiM === 'undefined' || typeof phiTheta === 'undefined') {
    return undefined;
  }
  return _phaseFrosPh2(wind2m, surfacePlusNearLoad(vars), vars.elevated_height, phiM, phiTheta);
};

const _calculateFROS_PH3 = (wd, vars) => {
  const wind10m = wd[PREFIX_WIND_MAG_KPH];
  const phiM = _calculatePHI_M(wd, vars);
  const phiTheta = _calculatePHI_THETA(wd, vars);
  if (typeof wind10m === 'undefined' || typeof phiM === 'undefined' || typeof phiTheta === 'undefined') {
    return undefined;
  }
  return _phaseFrosPh3(wind10m, phiM, phiTheta);
};

const _calculateP_PH2 = (wd, vars) => {
  const wind2m = _calculateWIND_2M(wd, vars);
  const phiM = _calculatePHI_M(wd, vars);
  if (typeof wind2m === 'undefined' || typeof phiM === 'undefined') {
    return undefined;
  }
  return _probabilityPh2(wind2m, phiM, surfacePlusNearLoad(vars));
};

const _calculateP_PH3 = (wd, vars) => {
  const frosPh2 = _calculateFROS_PH2(wd, vars);
  const wind10m = wd[PREFIX_WIND_MAG_KPH];
  const phiM = _calculatePHI_M(wd, vars);
  if (typeof frosPh2 === 'undefined' || typeof wind10m === 'undefined' || typeof phiM === 'undefined') {
    return undefined;
  }
  return _probabilityPh3(frosPh2, wind10m, phiM);
};

const _calculateEXP_FROS = (wd, vars) => {
  const frosPh1 = _calculateFROS_PH1(wd, vars);
  const frosPh2 = _calculateFROS_PH2(wd, vars);
  const frosPh3 = _calculateFROS_PH3(wd, vars);
  const pPh2 = _calculateP_PH2(wd, vars);
  const pPh3 = _calculateP_PH3(wd, vars);
  if (typeof frosPh1 === 'undefined' ||
      typeof frosPh2 === 'undefined' ||
      typeof frosPh3 === 'undefined' ||
      typeof pPh2 === 'undefined' ||
      typeof pPh3 === 'undefined') {
    return undefined;
  }
  return _expectedFros(frosPh1, frosPh2, frosPh3, pPh2, pPh3);
};

const _calculateEXP_FH = (wd, vars) => {
  const expFros = _calculateEXP_FROS(wd, vars);
  if (typeof expFros === 'undefined') {
    return undefined;
  }
  return 0.0193 * Math.pow(expFros, 0.723) * Math.exp(0.64 * vars.elevated_height) * 1.07;
};

const _calculateFLANK_ROS = (wd, vars) => {
  const expFros = _calculateEXP_FROS(wd, vars);
  const wind10m = wd[PREFIX_WIND_MAG_KPH];
  if (typeof expFros === 'undefined' || typeof wind10m === 'undefined') {
    return undefined;
  }
  return expFros / (0.2204 * wind10m + 1);
};

const _calculateFI = (wd, vars) => {
  const expFros = _calculateEXP_FROS(wd, vars);
  const tfl = calcTotalFuelLoad(vars);
  if (typeof expFros === 'undefined') {
    return undefined;
  }
  return 18600 * expFros / 3600 * tfl / 10;
};

// Final column set and labels from tmp/outputcolumns.txt; intermediates are All-only.
const PHI_MD_DISPLAY_NAME = 'Moisture Spread Coeff (ΦMd)';
const PHI_MD_WET_DISPLAY_NAME = 'Wet Moisture Spread Coeff (ΦMd)';

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread (°TN)', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  { key: 'FFM_PM', displayName: 'FMC Summer PM (%)', calc: calculateFFM_PM, decPlaces: 1 },
  { key: 'FFM_DT', displayName: 'FMC Daytime (%)', calc: calculateFFM_DT, decPlaces: 1 },
  { key: 'FFM_NT', displayName: 'FMC Nighttime (%)', calc: calculateFFM_NT, decPlaces: 1 },
  { key: 'FFM_C', displayName: 'Current Fine Fuel Moisture (%)', calc: calculateFFM_C, decPlaces: 1 },
  { key: 'DF_WET', displayName: 'Drought Factor Wet Forest', calc: _calculateDF_WET, decPlaces: 2 },
  { key: 'FROS_PH1', displayName: 'Phase I FROS (m/h)', calc: _calculateFROS_PH1, decPlaces: 0 },
  { key: 'FROS_PH2', displayName: 'Phase II FROS (m/h)', calc: _calculateFROS_PH2, decPlaces: 0 },
  { key: 'FROS_PH3', displayName: 'Phase III FROS (m/h)', calc: _calculateFROS_PH3, decPlaces: 0 },
  { key: 'P_PH2', displayName: 'Prob of Phase II', calc: _calculateP_PH2, decPlaces: 2 },
  { key: 'P_PH3', displayName: 'Prob of Phase III', calc: _calculateP_PH3, decPlaces: 2 },
  {
    key: 'EXP_FROS', displayName: 'Expected FROS (m/h)', calc: _calculateEXP_FROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 1000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  {
    key: 'EXP_FH', displayName: 'Expected Flame Height (m)', calc: _calculateEXP_FH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  { key: 'FLANK_ROS', displayName: 'Flank ROS (m/h)', calc: _calculateFLANK_ROS, decPlaces: 0 },
  {
    key: 'FI', displayName: 'Fireline Intensity (kW/m)', calc: _calculateFI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'WIND_2M', displayName: 'Wind @ 2m (km/h)', calc: _calculateWIND_2M, decPlaces: 1, nonFinal: true },
  { key: 'PHI_MD', displayName: PHI_MD_DISPLAY_NAME, calc: _calculatePHI_MD, decPlaces: 3, nonFinal: true },
  { key: 'DF', displayName: 'Drought Factor', calc: getDf, decPlaces: 1, nonFinal: true },
  { key: 'PHI_FA', displayName: 'Fuel Availability (ΦFA)', calc: _calculatePHI_FA, decPlaces: 3, nonFinal: true },
  { key: 'PHI_M', displayName: 'Combined Moisture (ΦM)', calc: _calculatePHI_M, decPlaces: 3, nonFinal: true },
  { key: 'PHI_THETA', displayName: 'Slope Factor (Φθ)', calc: _calculatePHI_THETA, decPlaces: 2, nonFinal: true }
];

const valuesConfigForForestType = (forestType) =>
  forestType === FOREST_TYPE.DRY
    ? VALUES_CONFIG.filter(v => v.key !== 'DF_WET')
    : VALUES_CONFIG;

const buildMk2TableMetaData = (dataSet, forestType) =>
  valuesConfigForForestType(forestType).map(v => ({
    key: v.key,
    displayName: v.key === 'PHI_MD' && forestType === FOREST_TYPE.WET ?
      PHI_MD_WET_DISPLAY_NAME :
      v.displayName,
    decPlaces: v.decPlaces || 0,
    colors: v.colors,
    formatFn: v.formatFn,
    cellGraph: v.cellGraph,
    nonFinal: v.nonFinal,
    arrow: v.arrow,
    data_set: dataSet
  }));

const GRAPHS_CONFIG = [
  {
    title: 'Expected Rate of Spread',
    id: 'EXP_FROS',
    type: 'LineChart',
    YAxisLabel: 'm/h',
    series: [
      {
        name: 'Expected FROS',
        dataKey: 'EXP_FROS',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      },
      {
        name: 'Phase I FROS',
        dataKey: 'FROS_PH1',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      },
      {
        name: 'Flank ROS',
        dataKey: 'FLANK_ROS',
        type: 'monotone',
        stroke: '#7AC142',
        active: true
      }
    ]
  }
];

const extractVariables = (state) => ({
  forest_type: state.forest_type,
  lag_temp_rh: state.lag_temp_rh,
  summer_afternoon_fmc: state.summer_afternoon_fmc,
  slope: state.slope,
  surface_hazard: state.surface_hazard,
  near_surface_hazard: state.near_surface_hazard,
  near_surface_height: state.near_surface_height,
  elevated_hazard: state.elevated_hazard,
  elevated_height: state.elevated_height,
  bark_hazard: state.bark_hazard,
  wind_reduction_factor: state.wind_reduction_factor,
  overstorey_cover: state.overstorey_cover,
  overstorey_height: state.overstorey_height,
  tall_understorey_cover: state.tall_understorey_cover,
  aspect: state.aspect,
  slope_steepness: state.slope_steepness
});

export function createVestaMk2Reducer(dataSet, defaultVariables) {
  const valuesConfig = valuesConfigForForestType(defaultVariables.forest_type);
  const initialState = {
    ...defaultVariables,
    calculated_fuel_load: totalFuelLoad(defaultVariables),
    table_data: {},
    graph_data: emptyGraphMap(GRAPHS_CONFIG),
    table_meta_data: buildMk2TableMetaData(dataSet, defaultVariables.forest_type),
    graph_meta_data: GRAPHS_CONFIG
  };

  return (state = initialState, action) => {
    switch (action.type) {
      case ActionTypes.SET_BEHAVIOUR_VARIABLES:
        if (action.data_set === dataSet) {
          const merged = { ...extractVariables(state), ...action.variables };
          merged.forest_type = state.forest_type;
          return {
            ...state,
            ...merged,
            calculated_fuel_load: totalFuelLoad(merged)
          };
        }
        break;
      case ActionTypes.CALCULATE_VESTA_MK2:
        if (action.data_set === dataSet) {
          const modifications = action.modifications ? action.modifications.properties.data : {};
          const variables = extractVariables(state);
          const new_table_data =
            calculateTableData(valuesConfig,
              action.include_history,
              action.short_future,
              action.weather_data.table_data,
              variables,
              modifications);
          const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
          const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

          return {
            ...state,
            ticks,
            graph_data: new_graph_data,
            table_data: new_table_data,
            calculated_fuel_load: totalFuelLoad(variables)
          };
        }
        break;
      default:
        return state;
    }
    return state;
  };
}



// WEBPACK FOOTER //
// ./src/reducers/point_data_vesta_mk2.js